import { useCallback, useMemo, useState } from 'react'

/**
 * The draft / value / reset dance every review drawer was repeating.
 *
 * `value` is the draft merged over the existing record merged over the
 * defaults, so an unedited field always reflects the server record even if it
 * changes underneath an open drawer — which is what you want when an
 * optimistic verdict lands while someone is editing a description.
 */
export function useEntityDraft<T extends object>(
  existing: T | null,
  defaults: Partial<T>,
): {
  value: T
  set: <K extends keyof T>(key: K, v: T[K]) => void
  patch: (next: Partial<T>) => void
  reset: () => void
  dirty: boolean
} {
  const [draft, setDraft] = useState<Partial<T>>({})

  const value = useMemo(
    () => ({ ...defaults, ...(existing ?? {}), ...draft }) as T,
    // `defaults` is normally an inline literal; freezing it per existing record
    // is intentional — a new identity each render would reset nothing, since
    // the spread result is only ever read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [existing, draft],
  )

  const set = useCallback(<K extends keyof T>(key: K, v: T[K]) => {
    setDraft((d) => ({ ...d, [key]: v }))
  }, [])

  const patch = useCallback((next: Partial<T>) => {
    setDraft((d) => ({ ...d, ...next }))
  }, [])

  const reset = useCallback(() => setDraft({}), [])

  return { value, set, patch, reset, dirty: Object.keys(draft).length > 0 }
}
